import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { Check } from "lucide-react";

interface OptionCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  banner?: string;
  disabled?: boolean;
  // Content alignment. Defaults to "center" (the wizard steps); "left" keeps the
  // selection check in the corner but left-aligns the icon/title/description.
  align?: "center" | "left";
  // Tighter padding + smaller icon for denser layouts (e.g. accordion rows).
  compact?: boolean;
  className?: string;
}

export function OptionCard({
  icon,
  title,
  description,
  selected,
  onClick,
  banner,
  disabled = false,
  align = "center",
  compact = false,
  className,
}: OptionCardProps) {
  const active = selected && !disabled;
  const left = align === "left";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        "group relative flex w-full flex-col rounded-lg border-2 transition-[border-color,background-color,box-shadow] duration-200 ease-out",
        left
          ? "items-start text-left"
          : "items-center justify-center text-center",
        compact
          ? "gap-2 px-4 py-4"
          : left
            ? "gap-2.5 px-4 py-4"
            : "gap-2.5 px-4 pb-6 pt-7",
        disabled && "cursor-not-allowed opacity-50 border-border bg-surface",
        !disabled && "cursor-pointer shadow-sm",
        // Selection reads from the primary accent + the corner check — not a
        // yellow fill, which was too close to the hover tint (Guided Setup
        // ticket). Hover stays neutral so a hovered neighbour can't look chosen.
        active && "border-primary bg-primary/[0.04] ring-1 ring-primary/15",
        !disabled &&
          !selected &&
          "border-border bg-surface hover:border-primary/20 hover:bg-muted/40",
        className,
      )}
    >
      {/* Selection indicator — a radio-style circle that fills with a check
          when chosen, giving an unambiguous "this one is selected" signal. */}
      <span
        aria-hidden
        className={cn(
          "absolute right-2 top-2 flex size-5 items-center justify-center rounded-full border transition-all duration-200",
          active
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border bg-background",
        )}
      >
        {active && (
          <Check className="size-3 stroke-[3] motion-safe:animate-in motion-safe:zoom-in-75 motion-safe:duration-200" />
        )}
      </span>

      <div
        className={cn(
          "flex items-center justify-center",
          compact ? "size-9" : "size-12",
          active ? "text-primary" : "text-text-muted",
        )}
      >
        {icon}
      </div>

      <div
        className={cn(
          "flex flex-col gap-1",
          left ? "items-start" : "items-center",
        )}
      >
        {banner && (
          <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary poppins">
            {banner}
          </span>
        )}
        <p
          className={cn(
            "font-semibold poppins leading-none",
            compact ? "text-sm tracking-tight" : "text-md tracking-wider",
            active ? "text-primary" : "text-text-primary",
          )}
        >
          {title}
        </p>
        <p
          className={cn(
            "text-sm text-text-muted leading-relaxed mulish",
            !left && "px-4",
          )}
        >
          {description}
        </p>
      </div>
    </button>
  );
}
